import { useState } from 'react';
import { Menu, X, Wrench } from 'lucide-react';
import type { UseSetups } from '@/hooks/useSetups';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import { useTuningSession } from './hooks/useTuningSession';
import { Sidebar } from './components/Sidebar';
import { StepShell } from './components/StepShell';
import { GoalCard, TipCard, WarningCard } from './components/Cards';
import { STEP_REGISTRY, getStepDefinition } from './steps/registry';
import { PreflightStep } from './steps/PreflightStep';
import { ArrowPrepStep } from './steps/ArrowPrepStep';
import { SightPeepStep } from './steps/SightPeepStep';
import { BowGripStep } from './steps/BowGripStep';
import { CamTimingStep } from './steps/CamTimingStep';
import { TillerStep } from './steps/TillerStep';
import { CenterShotStep } from './steps/CenterShotStep';
import { NockingDLoopStep } from './steps/NockingDLoopStep';
import { RestBranchStep } from './steps/RestBranchStep';
import { RestLimbDrivenStep } from './steps/RestLimbDrivenStep';
import { RestCableDrivenStep } from './steps/RestCableDrivenStep';
import { PaperTuneStep } from './steps/PaperTuneStep';
import { BareshaftStep } from './steps/BareshaftStep';
import { NockTuningStep } from './steps/NockTuningStep';
import { WalkBackStep } from './steps/WalkBackStep';
import { BroadheadGroupStep } from './steps/BroadheadGroupStep';
import { FinalReportStep } from './steps/FinalReportStep';
import { CheatSheetButton } from './components/CheatSheets';
import type { StepId } from './types';

interface TuningWizardProps {
  setups: UseSetups;
}

/**
 * Bow Tuning Wizard — the new "Tune" tab.
 *
 * Architecture: lazy-loaded by ArrowBuilder when the user clicks the Tune tab.
 * Auto-binds to the active Setup. Sidebar provides clickable navigation;
 * StepShell wraps each step content with consistent chrome.
 *
 * Most steps are not yet implemented — they render a placeholder via
 * PlaceholderStep. The wizard ships with step 1 (Pre-flight) fully wired so
 * Sal can click through the skeleton + feel the UX before the full step
 * content lands.
 */
export function TuningWizard({ setups }: TuningWizardProps) {
  const setup = setups.activeSetup;
  const tuning = useTuningSession(setup?.id ?? null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  if (!setup) {
    return (
      <EmptyState
        icon={<Wrench size={24} />}
        title="Pick a setup to tune"
        description="Tuning is bound to a Setup (your bow + sight + arrow). Pick or create a setup in the Builder tab, then come back here."
      />
    );
  }

  const visibleSteps = STEP_REGISTRY.filter((s) => {
    if (s.id === 'rest_limb_driven') return tuning.session.branches.restType === 'limb_driven';
    if (s.id === 'rest_cable_driven') return tuning.session.branches.restType === 'cable_driven';
    return true;
  });
  const currentIdx = visibleSteps.findIndex((s) => s.id === tuning.session.currentStep);
  const currentStep = visibleSteps[currentIdx] ?? visibleSteps[0]!;
  const stepDef = getStepDefinition(currentStep.id);
  const isComplete = tuning.session.completedSteps.includes(currentStep.id);

  const handleNext = () => {
    tuning.markStepComplete(currentStep.id);
  };

  const handleBack = () => {
    const prev = visibleSteps[Math.max(0, currentIdx - 1)];
    if (prev) tuning.setCurrentStep(prev.id);
  };

  const handleSaveExit = () => {
    // Auto-save runs constantly; this just navigates back to Builder.
    // We dispatch a custom event the parent (ArrowBuilder) can listen for.
    window.dispatchEvent(new CustomEvent('sparrow:tuning-save-exit'));
  };

  const handleStepClick = (stepId: StepId) => {
    tuning.setCurrentStep(stepId);
    setMobileSidebarOpen(false);
  };

  return (
    <div className="relative lg:flex lg:gap-0 -mx-2 sm:-mx-6">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => setMobileSidebarOpen(true)}
          className="p-1.5 rounded text-zinc-400 hover:text-zinc-100"
          aria-label="Open step menu"
        >
          <Menu size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            Step {currentIdx + 1} of {visibleSteps.length}
          </p>
          <p className="text-sm font-semibold truncate">{stepDef.title}</p>
        </div>
        <div className="h-1.5 w-16 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-ember-500 transition-all"
            style={{ width: `${Math.round((tuning.session.completedSteps.length / visibleSteps.length) * 100)}%` }}
          />
        </div>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        >
          <div
            className="absolute inset-y-0 left-0 w-72 bg-zinc-950 border-r border-zinc-800 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end p-2">
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="p-1.5 rounded text-zinc-400 hover:text-zinc-100"
              >
                <X size={18} />
              </button>
            </div>
            <Sidebar session={tuning.session} activeStep={currentStep.id} onStepClick={handleStepClick} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar session={tuning.session} activeStep={currentStep.id} onStepClick={handleStepClick} />
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 min-h-[calc(100vh-220px)]">
        <StepShell
          stepNumber={currentIdx + 1}
          totalSteps={visibleSteps.length}
          title={stepDef.title}
          goal={stepDef.goal}
          onBack={handleBack}
          onNext={handleNext}
          onSaveExit={handleSaveExit}
          canGoNext={true}
          isFirstStep={currentIdx === 0}
          isLastStep={currentIdx === visibleSteps.length - 1}
          isComplete={isComplete}
          extraFooterActions={<CheatSheetButton />}
        >
          {renderStep(currentStep.id, tuning, setup, setups)}
        </StepShell>
      </main>
    </div>
  );
}

function renderStep(
  stepId: StepId,
  tuning: ReturnType<typeof useTuningSession>,
  setup: NonNullable<UseSetups['activeSetup']>,
  setups: UseSetups,
) {
  if (stepId === 'preflight') {
    return (
      <PreflightStep
        tuning={tuning}
        setup={setup}
        onUpdateSetupBow={(patch) => setups.updateSetup(setup.id, patch)}
      />
    );
  }
  if (stepId === 'arrow_prep') return <ArrowPrepStep tuning={tuning} setups={setups} />;
  if (stepId === 'bow_grip') return <BowGripStep tuning={tuning} />;
  if (stepId === 'cam_timing') return <CamTimingStep tuning={tuning} setup={setup} />;
  if (stepId === 'tiller') return <TillerStep tuning={tuning} setup={setup} />;
  if (stepId === 'center_shot') return <CenterShotStep tuning={tuning} setup={setup} />;
  if (stepId === 'nocking_d_loop') return <NockingDLoopStep tuning={tuning} />;
  if (stepId === 'rest_branch') return <RestBranchStep tuning={tuning} />;
  if (stepId === 'rest_limb_driven') return <RestLimbDrivenStep tuning={tuning} />;
  if (stepId === 'rest_cable_driven') return <RestCableDrivenStep tuning={tuning} />;
  if (stepId === 'paper_tune') return <PaperTuneStep tuning={tuning} />;
  if (stepId === 'bareshaft') return <BareshaftStep tuning={tuning} />;
  if (stepId === 'nock_tuning') return <NockTuningStep tuning={tuning} />;
  if (stepId === 'sight_peep') return <SightPeepStep tuning={tuning} />;
  if (stepId === 'walk_back') return <WalkBackStep tuning={tuning} />;
  if (stepId === 'broadhead_group') return <BroadheadGroupStep tuning={tuning} />;
  if (stepId === 'final_report') return <FinalReportStep tuning={tuning} />;
  return <PlaceholderStep stepId={stepId} tuning={tuning} />;
}

/**
 * Stub renderer for steps we haven't built yet. Shows the goal + a "coming soon"
 * note so Sal can click through the wizard skeleton and confirm the UX feel.
 */
function PlaceholderStep({
  stepId,
  tuning,
}: {
  stepId: StepId;
  tuning: ReturnType<typeof useTuningSession>;
}) {
  const def = getStepDefinition(stepId);
  return (
    <>
      <GoalCard title="What we're trying to achieve">
        {def.goal}
      </GoalCard>

      {stepId === 'rest_branch' && (
        <Card padding="lg">
          <h3 className="font-semibold mb-3">Pick your rest type</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <RestTypeCard
              label="Limb-Driven"
              tagline="Tied to the limb. Simpler, more forgiving, fail-safe."
              selected={tuning.session.branches.restType === 'limb_driven'}
              onClick={() => tuning.setRestType('limb_driven')}
            />
            <RestTypeCard
              label="Cable-Driven"
              tagline="Tied to the buss cable. Smoother, more adjustable."
              selected={tuning.session.branches.restType === 'cable_driven'}
              onClick={() => tuning.setRestType('cable_driven')}
            />
          </div>
        </Card>
      )}

      <TipCard title="Step coming soon" defaultOpen>
        We're building this step out next. The wizard chrome, sidebar navigation, branch
        selection, save & exit, and progress tracking are all live — content fills in
        over the coming releases.
      </TipCard>

      <WarningCard title="Safety reminder">
        Never adjust limbs, cables, or strings on a strung bow without a bow press.
        Never dry-fire a compound. When in doubt, take it to a certified pro shop.
      </WarningCard>
    </>
  );
}

function RestTypeCard({
  label,
  tagline,
  selected,
  onClick,
}: {
  label: string;
  tagline: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'text-left rounded-lg border p-4 transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10'
          : 'border-zinc-800 bg-zinc-950 hover:border-zinc-700')
      }
    >
      <p className={'font-semibold ' + (selected ? 'text-ember-500' : 'text-zinc-100')}>
        {label}
      </p>
      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{tagline}</p>
    </button>
  );
}
